import { LegalPage, LegalSection, LegalToc } from './LegalPage';
import { PageLink, ROUTES } from '../router';
import { INVITE_EXPIRY_DAYS } from '../data/legal';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import {
  POLICY_DATES,
  PUBLISHING_ENTITY,
  SERVICE_PROVIDERS,
  SUPPORT_EMAIL,
} from '../data/legal';

const mailto = `mailto:${SUPPORT_EMAIL}`;

const SupportEmail = () => <a href={mailto}>{SUPPORT_EMAIL}</a>;

/*
 * The published privacy policy.
 *
 * Every statement here describes behaviour that exists in the Tulink codebase —
 * the mobile app, the backend and this website. Retention periods are the real
 * configured ones. Where a control is not built yet it is not described as if
 * it were; the account-deletion route this page points at is the web request
 * form, because that is the route that actually works today.
 *
 * Section ids are a stable contract: Play Console, support replies and the app
 * link to individual clauses, so they must not drift when the copy is edited.
 */
const SECTIONS = [
  {
    id: 'about',
    title: 'About this policy',
    body: (
      <>
        <p>
          This policy explains how {PUBLISHING_ENTITY} handles personal data across the Tulink mobile
          app, the Tulink business dashboard and this website. Tulink is a group-travel app: people
          travelling together share a live view of where each vehicle in the group is, so they can
          set off together, stay together on the road and know when everyone has arrived.
        </p>
        <p>
          Tulink is the developer named on the app’s store listings and is responsible for the data
          described here. If anything below is unclear, or you want to exercise any of the rights in{' '}
          <a href="#your-choices">Your choices and rights</a>, write to <SupportEmail /> and a person
          will answer you.
        </p>
        <p>
          Sharing your location is the point of the product, so this policy is specific about it. If
          you only read one section, read <a href="#location">Location data</a>.
        </p>
      </>
    ),
  },
  {
    id: 'data-we-collect',
    title: 'Data we collect',
    body: (
      <>
        <h3>Your account</h3>
        <p>
          When you create a Tulink account we store your email address, the display name you choose,
          a phone number if you add one, whether your email and phone have been verified, and the
          dates your account was created, last updated and last signed out. Your account also carries
          the identifier our sign-in provider issues for you.
        </p>
        <p>
          You can register with an email address and password, or sign in with Google or Apple. If
          you sign in with Apple and choose to hide your email address, we store the private relay
          address Apple gives us, exactly as supplied. We never see or store the password you use
          with Google or Apple, and your Tulink password is handled by our sign-in provider rather
          than held by us in a readable form.
        </p>

        <h3>Journeys</h3>
        <p>
          A journey record holds its name and invite code, who leads it and who is taking part, each
          participant’s role and status, the schedule and the start and end times, the destination
          coordinates and address, the distance at which the app decides someone is lagging behind,
          when each person arrived and when they connected or disconnected, and any alerts raised
          along the way. When a journey finishes we also keep summary figures for it: how long it
          took, how far it went, the average speed, the largest gap that opened up between vehicles,
          the number of lag alerts and how many people took part.
        </p>

        <h3>Location</h3>
        <p>
          While a journey is running the app collects and shares your precise position. This is set
          out in full in <a href="#location">Location data</a>.
        </p>

        <h3>Your device</h3>
        <p>
          To send you push notifications we store the messaging token your device is issued, which
          platform it is (Android or iOS) and, where the app supplies one, a device identifier.
          Journey participation records may also hold the platform and app version you are running,
          so we can tell which build a problem came from.
        </p>

        <h3>Diagnostics</h3>
        <p>
          Outside development builds, the app sends crash and error reports so we can find out why it
          stopped working. Our servers keep operational logs of requests, which include account and
          journey identifiers, connection identifiers and technical details about what the server was
          doing. These logs exist to keep the service working and secure. We do not use them to build
          a profile of you, and we do not use them for advertising.
        </p>
        <p>
          Because live journeys are location-driven, diagnostic records about a journey can in some
          circumstances include position data. We keep those logs for a short, fixed period, restrict
          who can read them, and use them only to diagnose and fix problems — see{' '}
          <a href="#retention">How long we keep data</a>.
        </p>

        <h3>This website</h3>
        <p>
          This website has no accounts, no analytics or advertising scripts, and it does not set
          cookies. The only personal data it handles is what you type into the{' '}
          <PageLink to={ROUTES.deleteAccount}>account deletion request form</PageLink>: the email
          address on your account, an optional name and reason, and your explicit confirmation that
          you want the account deleted.
        </p>
        <p>
          <PageLink to={ROUTES.android}>Android testing</PageLink> happens off this site, on Google
          Groups and Google Play. When you join the Tulink Testers group and opt in, we see the
          Google account email address you used.
        </p>
        <p>
          Account deletion forms are delivered to us by email rather than stored on this site. See{' '}
          <a href="#sharing">Sharing and third parties</a>. As with any website, the servers and
          networks that serve these pages keep ordinary request logs, which can include your IP
          address, your browser’s user agent, the address you requested and the time you requested
          it.
        </p>

        <h3>The business dashboard</h3>
        <p>
          Organisations that use Tulink to coordinate their own travel sign in to a separate
          dashboard. Those operator accounts are handled by a different sign-in provider and hold the
          operator’s identity and the organisation they belong to. If you are a traveller using the
          mobile app, this does not apply to you, except that if you join an organisation its
          authorised operators can see some of your data, as described in{' '}
          <a href="#organisations">Organisations you join</a>.
        </p>
      </>
    ),
  },
  {
    id: 'location',
    title: 'Location data',
    body: (
      <>
        <p>
          Tulink collects <strong>precise</strong> location, and it collects it{' '}
          <strong>in the background</strong> — while the app is not on screen and while your phone is
          locked — but only while you are on an active journey.
        </p>

        <h3>When location is collected</h3>
        <p>
          Location collection starts when a journey you are part of becomes active, and stops when
          that journey ends, when you leave it, or when you withdraw location permission in your
          device settings. Tulink does not track you between journeys.
        </p>
        <p>
          On Android, a journey runs as a foreground service, which means your phone shows a
          permanent notification saying Tulink is sharing your location. On iOS you will see the
          system’s background location indicator. These are deliberate: if Tulink has your location,
          your phone tells you so.
        </p>

        <h3>How often, and what is sent</h3>
        <p>
          During a journey the app asks your phone for navigation-grade positioning and takes a
          reading each time you move about five metres. It takes one reading as the journey starts so
          you appear to the group straight away, sends movement updates at most once a second, and
          sends a periodic update even when you are stationary so the group can still see you parked.
        </p>
        <p>Each update contains:</p>
        <ul>
          <li>your latitude and longitude</li>
          <li>the time of the reading</li>
          <li>how accurate your phone believes the reading is</li>
          <li>your altitude, heading and speed</li>
          <li>whether you are currently moving</li>
          <li>your battery level, where your device reports it</li>
        </ul>
        <p>
          Battery level is included so the rest of the group can tell the difference between someone
          who has stopped and someone whose phone is about to die.
        </p>

        <h3>Who can see it</h3>
        <p>
          Your live position during a journey is shown to the other members of that journey. That is
          the purpose of the product. Nobody outside the journey sees your live position, except the
          authorised dashboard operators of an organisation the journey belongs to, or of an
          organisation you have joined. See <a href="#organisations">Organisations you join</a>.
        </p>

        <h3>What is kept afterwards</h3>
        <p>
          Live positions are held in a fast, short-lived store that discards each one five minutes
          after it arrives, and the whole live record for a journey is cleared when the journey ends
          or is cancelled.
        </p>
        <p>
          Separately, we keep a durable location history for each journey in our database. It is what
          makes journey history, arrival and lag detection, and the end-of-journey summary possible.
          It is retained as described in <a href="#retention">How long we keep data</a> and removed
          when you delete your account.
        </p>

        <h3>How to stop it</h3>
        <p>
          End or leave the journey, or turn off location permission for Tulink in your device
          settings. Either stops the sharing immediately. Tulink cannot collect your location without
          the permission you granted, and you can take it back at any time — although without it the
          app cannot do the one thing it is for.
        </p>
      </>
    ),
  },
  {
    id: 'organisations',
    title: 'Organisations you join',
    body: (
      <>
        <p>
          A business or group using the Tulink dashboard can invite you to join its organisation.
          You decide whether to join. You can belong to one organisation at a time.
        </p>

        <h3>Invitations</h3>
        <p>
          When an organisation’s admin invites you, Tulink tries to send you a notification and the
          invite waits in the app’s Invites tab. Before you join, the app tells you what the
          organisation will be able to see. An invite can be accepted for {INVITE_EXPIRY_DAYS} days.
          Until you accept, the organisation cannot see any of your journeys. When you accept or
          decline, the admin who invited you is told by email, with your display name.
        </p>

        <h3>What the organisation sees once you join</h3>
        <ul>
          <li>your display name and email address, in its list of members;</li>
          <li>
            the journeys run under the organisation, including your live position while such a
            journey is active, and the journey history and reports afterwards;
          </li>
          <li>
            your position on other journeys you take part in after joining, including ones started
            by people outside the organisation. On those journeys it sees only its own members’
            positions, not anyone else’s;
          </li>
          <li>routes you record and save, which are shared with everyone in the organisation.</li>
        </ul>
        <p>
          When you join, journeys you are leading that have not finished yet are added to the
          organisation. The organisation cannot see your location between journeys, and it cannot
          see journeys you took part in before you joined, other than ones that were already run
          under it. Some operators see only the members an admin has assigned to them.
        </p>

        <h3>Leaving</h3>
        <p>
          You can leave at any time from your profile in the app, and an admin can remove you. After
          that, the organisation no longer sees your new journeys or your position on them, and its
          saved routes disappear from your app. Journeys that were already run under the organisation
          stay in its history, and routes you saved stay available to it.
        </p>
      </>
    ),
  },
  {
    id: 'how-we-use-data',
    title: 'How we use data',
    body: (
      <>
        <p>We use personal data for these purposes and no others:</p>
        <ul>
          <li>
            <strong>To run your account.</strong> Creating it, signing you in, verifying your email
            address, resetting your password and keeping your profile up to date.
          </li>
          <li>
            <strong>To run a shared journey.</strong> Showing group members where each other are,
            working out who is lagging behind, detecting arrivals, and drawing the map and route.
          </li>
          <li>
            <strong>To search for places and plan routes.</strong> Turning what you type into a
            destination, and calculating distances and directions to it.
          </li>
          <li>
            <strong>To notify you.</strong> Journey invitations, reminders, lag and arrival alerts,
            and operational email about your account.
          </li>
          <li>
            <strong>To give you your journey history.</strong> The record of past journeys and their
            summary figures, which we also use in aggregate to understand how well the product works.
          </li>
          <li>
            <strong>To keep the service working and secure.</strong> Diagnosing crashes, investigating
            faults, preventing abuse and protecting accounts.
          </li>
          <li>
            <strong>To support you.</strong> Answering the messages you send us and acting on
            requests such as account deletion.
          </li>
          <li>
            <strong>To meet legal obligations.</strong> Where the law requires us to keep or produce
            records.
          </li>
        </ul>
        <p>
          <strong>
            Tulink does not sell your personal data, and does not use it for third-party targeted
            advertising.
          </strong>{' '}
          The app contains no advertising software and no third-party advertising or analytics
          tracking is built into it. We do not share your location with advertisers, data brokers or
          anyone else outside the providers listed in{' '}
          <a href="#sharing">Sharing and third parties</a>.
        </p>
      </>
    ),
  },
  {
    id: 'sharing',
    title: 'Sharing and third parties',
    body: (
      <>
        <h3>Other people in your group</h3>
        <p>
          Tulink is a shared view by design. When you join a journey, the other members of that
          journey can see your display name, your role and status in the group, and — while the
          journey is active — your live position and the details that come with it, including whether
          you are moving and your battery level. If the journey belongs to an organisation, or you
          belong to one, its authorised dashboard operators can also see journey data as described in{' '}
          <a href="#organisations">Organisations you join</a>.
        </p>

        <h3>Service providers</h3>
        <p>
          Tulink is built on services run by other companies. Each one receives only what it needs to
          do its job, and each is bound by its own privacy terms.
        </p>
        <dl className="tl-legal__providers">
          {SERVICE_PROVIDERS.map((provider) => (
            <div key={provider.name}>
              <dt>
                <a href={provider.privacyUrl} target="_blank" rel="noopener noreferrer">
                  {provider.name}
                </a>
              </dt>
              <dd>{provider.role}</dd>
            </div>
          ))}
        </dl>
        <p>
          Beyond these, Tulink runs on hosting, database and network infrastructure that necessarily
          processes the data described in this policy in order to store and serve it. Those providers
          act on our instructions and are not permitted to use your data for their own purposes.
        </p>

        <h3>Everyone else</h3>
        <p>
          We do not share personal data with anyone else, except where we are legally required to —
          for example in response to a valid legal request — or where it is necessary to establish or
          defend a legal claim, or to protect someone’s safety.
        </p>
      </>
    ),
  },
  {
    id: 'security',
    title: 'Security',
    body: (
      <>
        <p>
          Traffic between the app, this website and our servers is encrypted in transit. Requests
          for account and journey data have to carry a valid sign-in token, and access is checked so
          that journey data is available only to authorised journey participants and, where
          applicable, authorised operators for the organisation running that journey.
        </p>
        <p>On your own device:</p>
        <ul>
          <li>
            Sign-in and refresh tokens are held in the platform’s secure storage — the Keychain on
            iOS, the equivalent hardware-backed keystore on Android — rather than in ordinary app
            files.
          </li>
          <li>
            The app’s offline data — saved routes, journey sessions, location updates queued while
            you were out of signal, map tile records and your journey history — is encrypted at rest
            with a key held in that same secure storage.
          </li>
        </ul>
        <p>
          On our side, access to production systems and logs is restricted to the people who need it
          to operate the service. No system is perfectly secure, and we do not claim otherwise. If a
          breach affects your personal data we will notify you and the relevant authorities where the
          law requires it.
        </p>
      </>
    ),
  },
  {
    id: 'retention',
    title: 'How long we keep data',
    body: (
      <>
        <p>Some periods are fixed by how the service is built:</p>
        <ul>
          <li>
            <strong>Live positions:</strong> discarded five minutes after they arrive. The live record
            for a journey is cleared when the journey ends or is cancelled.
          </li>
          <li>
            <strong>Cached place-search results:</strong> kept for up to seven days, then discarded.
          </li>
          <li>
            <strong>Server logs:</strong> production application logs rotate and are kept for up to
            fourteen days.
          </li>
          <li>
            <strong>Journey history on your phone:</strong> the app keeps at most your hundred most
            recent completed journeys. The saved route line for a journey is removed once that journey
            ends, and the app’s offline data for an account is cleared when you sign out or your
            session ends.
          </li>
        </ul>
        <p>Everything else is kept only as long as there is a reason to keep it:</p>
        <ul>
          <li>
            <strong>Your account and profile:</strong> for as long as your account exists.
          </li>
          <li>
            <strong>Journeys, participation records and location history:</strong> for as long as
            needed to give you your journey history and summaries, to keep the service working and
            secure, and to meet our legal obligations.
          </li>
          <li>
            <strong>Email you send us:</strong> for as long as it takes to deal with what you asked
            about, and to keep a record that we dealt with it.
          </li>
        </ul>
        <p>
          When you ask us to delete your account, what happens to this data is described in{' '}
          <a href="#deletion">Deleting your account and data</a>.
        </p>
      </>
    ),
  },
  {
    id: 'deletion',
    title: 'Deleting your account and data',
    body: (
      <>
        <p>
          You can ask us to delete your Tulink account and the personal data associated with it. The
          request page is public: you do not have to be signed in to use it, and it never asks for
          your password, your location or an identity document.
        </p>
        <div className="tl-legal__callout">
          {/* The path is spelled out because the button is hidden when printed. */}
          <p>
            Use the account deletion request page at <strong>/delete-account</strong>. You will only
            need the email address on your account. You can also email <SupportEmail /> from that
            address instead.
          </p>
          <PageLink className="tl-button tl-button--secondary" to={ROUTES.deleteAccount}>
            Delete your account
          </PageLink>
        </div>

        <h3>How we verify it is you</h3>
        <p>
          We contact the email address on the account to confirm the request is genuinely yours
          before we act on it. We cannot delete an account on the word of someone who has not proved
          they own it — that protection is the reason for the step.
        </p>

        <h3>What deletion removes</h3>
        <p>
          Once a request is verified, we delete or irreversibly anonymise the data associated with
          your account across our systems — your account and profile details, your journey
          participation, your location history, your push notification tokens and your journey
          history — and instruct the providers listed in{' '}
          <a href="#sharing">Sharing and third parties</a> to do the same with what they hold for us,
          so far as their services allow. The app’s stored data for your account on your own device
          is cleared as part of signing out.
        </p>
        <p>
          Deletion is permanent. It is not the same as signing out, and it is not a freeze,
          suspension or deactivation of your account — those leave your data in place, and Tulink
          never treats them as deletion.
        </p>

        <h3>What may remain</h3>
        <p>
          We may retain a narrow set of records after deletion where we have to: to prevent fraud and
          abuse, to keep the service secure, to resolve a dispute, or because the law requires it.
          Journey records that other people also took part in may continue to exist as their history,
          with your personal details removed from them. Routes you saved for an organisation belong to
          that organisation and stay available to it, without your name attached. Copies can also persist for a short time in
          routine backups and in the server logs described in{' '}
          <a href="#retention">How long we keep data</a> until those expire on their normal schedule.
        </p>
        <p>
          We act on verified requests as quickly as we reasonably can and will tell you when it is
          done. We do not quote a completion deadline here that we have not committed to.
        </p>
      </>
    ),
  },
  {
    id: 'your-choices',
    title: 'Your choices and rights',
    body: (
      <>
        <h3>Controls you have on your device</h3>
        <ul>
          <li>
            <strong>Location.</strong> Android and iOS both let you grant, restrict or withdraw
            Tulink’s location access — including background access — in your device settings at any
            time. Withdrawing it stops Tulink sharing your position.
          </li>
          <li>
            <strong>Notifications.</strong> Push notifications can be turned off for Tulink in the
            same settings.
          </li>
          <li>
            <strong>Sharing.</strong> Ending or leaving a journey stops location sharing without
            changing any permission.
          </li>
          <li>
            <strong>Organisations.</strong> You choose whether to accept an organisation’s
            invitation, and you can leave an organisation from your profile in the app at any time.
          </li>
          <li>
            <strong>Your details.</strong> You can update your profile details in the app.
          </li>
        </ul>

        <h3>Rights you can ask us to honour</h3>
        <p>
          Depending on where you live, data protection law may give you the right to ask for a copy
          of the personal data we hold about you, to have it corrected, to have it deleted, to
          receive it in a portable form, to object to or restrict certain processing, and to withdraw
          consent you previously gave. Where you have those rights, we honour them.
        </p>
        <p>
          To make any of these requests, write to <SupportEmail /> from the email address on your
          account, or use the{' '}
          <PageLink to={ROUTES.deleteAccount}>account deletion request page</PageLink>. We will
          verify the request is yours before acting on it, and we will answer within the time the
          applicable law allows. If you are unhappy with our answer, you may complain to the data
          protection authority for your country.
        </p>
      </>
    ),
  },
  {
    id: 'children',
    title: 'Children and the people Tulink is for',
    body: (
      <>
        <p>
          Tulink coordinates road travel between drivers and their passengers. It is intended for
          adults aged 18 and over, and it is not directed at children.
        </p>
        <p>
          We do not knowingly collect personal data from children. Tulink does not currently verify
          the age of the people who sign up, so if you believe a child has created an account, write
          to <SupportEmail /> and we will look into it and remove the account and its data.
        </p>
      </>
    ),
  },
  {
    id: 'international',
    title: 'International users',
    body: (
      <p>
        The providers and infrastructure Tulink runs on may store and process personal data in
        countries other than the one you are in, including countries whose data protection laws
        differ from your own. Where data moves across borders it is covered by the contractual terms
        and safeguards those providers offer, and it stays subject to this policy wherever it is
        processed.
      </p>
    ),
  },
  {
    id: 'changes',
    title: 'Changes to this policy',
    body: (
      <p>
        When this policy changes we update the effective and last-updated dates shown at the top of
        this page, and the current version is always the one published here. If a change materially
        affects how your personal data is used, we will give notice in the app as well, and where the
        law requires your consent we will ask for it before the change applies to you.
      </p>
    ),
  },
  {
    id: 'contact',
    title: 'Contact us',
    body: (
      <>
        <p>
          For any question about this policy, about the personal data we hold about you, or to
          exercise any of the rights described above, write to <SupportEmail />. A person reads that
          inbox.
        </p>
        <p>
          Please include the email address on your Tulink account so we can identify you and answer
          properly.
        </p>
      </>
    ),
  },
];

export const PrivacyPage = () => {
  useDocumentTitle('Tulink Privacy Policy');

  return (
    <LegalPage
      eyebrow="Legal"
      title="Tulink Privacy Policy"
      lede="How Tulink handles personal data across the Tulink app and this website."
      meta={[
        { term: 'Effective date', value: POLICY_DATES.effective },
        { term: 'Last updated', value: POLICY_DATES.updated },
        { term: 'Published by', value: PUBLISHING_ENTITY },
        { term: 'Privacy contact', value: SUPPORT_EMAIL },
      ]}
      aside={<LegalToc sections={SECTIONS} />}
    >
      {SECTIONS.map((section) => (
        <LegalSection key={section.id} id={section.id} title={section.title}>
          {section.body}
        </LegalSection>
      ))}
    </LegalPage>
  );
};
